import qs from 'query-string'
import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router'

// The value is seeded from the URL on mount and then held in local state, so it does not track
// later changes to the search string made from anywhere else.
export const useQueryParam = <T = qs.ParsedQuery[keyof qs.ParsedQuery]>(
  param: string,
  defaultValue?: T,
  {
    parse,
    stringify,
  }: {
    parse: (value: qs.ParsedQuery[keyof qs.ParsedQuery] | undefined) => T
    stringify: (value: T) => qs.ParsedQuery[keyof qs.ParsedQuery]
  } = {
    parse: value => value as T,
    stringify: value => value as qs.ParsedQuery[keyof qs.ParsedQuery],
  },
): [T | undefined, (newValue: T) => void] => {
  const navigate = useNavigate()
  const location = useLocation()
  const params = qs.parse(location.search)
  const value = parse(params[param])

  const [state, setState] = useState(value ?? defaultValue)

  return [
    state,
    newValue => {
      setState(newValue)
      void navigate(
        qs.stringifyUrl({
          url: location.pathname,
          query: { ...params, [param]: stringify(newValue) },
        }),
      )
    },
  ]
}
